import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Header from "../../../Header/Header";
import SideBar from "../../../SideBar/SideBar";
import Footer from "../../../Footer/Footer";
import defaultImage from "../../../../assets/img/profiles/icons8-test-account-90.png";

import StudentProfileView from "./StudentProfileView";
import AdminProfileView from "./AdminProfileView";
import SuperAdminProfileView from "./SuperAdminProfileView";

import { userInformation } from "../../../HttpRequest/userdetails";
import { getUserDocuments, getProfileDetails, getEducationDetails, getTestScores, uploadUserProfile } from "../../../HttpRequest/uploadDocuments";
import Swal from "sweetalert2";

import "./roleProfiles.css";

const ProfileTab = (passingId) => {
  const [userProfileUrl, setUserProfileUrl] = useState(defaultImage);
  const [profileData, setProfileData] = useState(null);
  const [activeRole, setActiveRole] = useState("student");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let userId =
      passingId?.location?.state > 100
        ? passingId.location.state
        : (sessionStorage.getItem("id") || localStorage.getItem("id"));

    const rawRole = (
      sessionStorage.getItem("userRole") ||
      sessionStorage.getItem("role") ||
      localStorage.getItem("userRole") ||
      localStorage.getItem("role") ||
      ""
    ).toLowerCase();

    if (rawRole.includes("superadmin") || rawRole.includes("super-admin") || rawRole.includes("super_admin")) {
      setActiveRole("superadmin");
    } else if (rawRole.includes("admin")) {
      setActiveRole("admin");
    } else {
      setActiveRole("student");
    }

    if (userId) {
      setLoading(true);
      Promise.all([
        userInformation(userId).catch(() => null),
        getProfileDetails(userId).catch(() => null),
        getEducationDetails(userId).catch(() => null),
        getTestScores(userId).catch(() => null)
      ]).then(([userRes, profRes, eduRes, testRes]) => {
        const baseUser = userRes?.data || {};
        const profDetails = profRes?.data || {};
        const eduDetails = eduRes?.data || {};
        const testDetails = testRes?.data || {};

        const combinedData = {
          ...baseUser,
          profileDetails: profDetails,
          educationDetails: eduDetails,
          testScores: testDetails
        };

        setProfileData(combinedData);

        const backendRole = (baseUser.role || baseUser.groupName || "").toLowerCase();
        if (backendRole.includes("superadmin") || backendRole.includes("super-admin") || backendRole.includes("super_admin")) {
          setActiveRole("superadmin");
        } else if (backendRole.includes("admin")) {
          setActiveRole("admin");
        }

        if (baseUser.profilePic) {
          downloadProfile(baseUser.id, baseUser.profilePic);
        }
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [passingId]);

  const downloadProfile = async (userId, key) => {
    const headers = new Headers({ 'Content-Type': 'application/octet-stream' });
    const options = { headers, responseType: 'blob' };
    try {
      const response = await getUserDocuments(userId, key, options);
      if (response && response.headers && response.data) {
        const contentType = response.headers['content-type'];
        const blob = new Blob([response.data], { type: contentType });
        if (blob instanceof Blob) {
          setUserProfileUrl(URL.createObjectURL(blob));
        }
      }
    } catch (error) {
      console.error("Error fetching profile image:", error);
    }
  };

  const handleUpdateProfile = (updatedData) => {
    setProfileData(prev => ({ ...prev, ...updatedData }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File Type",
        text: "Please select an image file (PNG, JPG, JPEG, WEBP).",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "Image size must be less than 5MB.",
      });
      return;
    }

    const userId =
      passingId?.location?.state > 100
        ? passingId.location.state
        : (sessionStorage.getItem("id") || localStorage.getItem("id"));

    if (!userId) {
      Swal.fire({
        icon: "error",
        title: "Session Expired",
        text: "User session expired. Please log in again.",
      });
      return;
    }

    // Instant local preview in UI
    const localUrl = URL.createObjectURL(file);
    setUserProfileUrl(localUrl);

    Swal.fire({
      title: "Uploading Photo...",
      text: "Saving profile picture to cloud storage...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await uploadUserProfile(userId, "profilePic", file);
      console.log("Photo upload result:", response);
      const s3Key = response?.data?.s3Key || response?.data;
      if (s3Key) {
        setProfileData(prev => ({ ...prev, profilePic: s3Key }));
      }

      Swal.fire({
        icon: "success",
        title: "Photo Uploaded!",
        text: "Your profile picture has been updated successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Photo upload error:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Could not upload photo to server. Please try again.",
      });
    }
  };

  return (
    <div className="main-wrapper">
      <Header />
      <SideBar />

      <div className="page-wrapper role-profile-wrapper">
        <div className="content container-fluid" style={{ paddingTop: "5rem", paddingBottom: "3rem" }}>
          
          {/* Header Title Bar */}
          <div className="d-flex align-items-center justify-content-between mb-4 border-bottom border-dark pb-3">
            <div>
              <h3 className="page-title m-0 font-weight-bold" style={{ fontSize: "1.5rem", color: "#FFE082", letterSpacing: "0.02em" }}>
                My Profile
              </h3>
              <ul className="breadcrumb m-0 mt-1" style={{ background: "transparent", padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to={activeRole === "superadmin" ? "/superadmin" : activeRole === "admin" ? "/admindashboard" : "/dashboard"} style={{ color: "#D4AF37", fontWeight: 600 }}>
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: "#a0a0a0", fontWeight: 500 }}>
                  Profile
                </li>
              </ul>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-warning" role="status" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Loading Profile Data...</span>
              </div>
              <p className="mt-3 text-light opacity-75 fw-bold">Loading user profile...</p>
            </div>
          ) : (
            <>
              {activeRole === "student" && (
                <StudentProfileView
                  profileData={profileData}
                  userProfileUrl={userProfileUrl}
                  onUpdateProfile={handleUpdateProfile}
                  onPhotoUpload={handlePhotoUpload}
                />
              )}

              {activeRole === "admin" && (
                <AdminProfileView
                  profileData={profileData}
                  userProfileUrl={userProfileUrl}
                  onUpdateProfile={handleUpdateProfile}
                  onPhotoUpload={handlePhotoUpload}
                />
              )}

              {activeRole === "superadmin" && (
                <SuperAdminProfileView
                  profileData={profileData}
                  userProfileUrl={userProfileUrl}
                  onUpdateProfile={handleUpdateProfile}
                  onPhotoUpload={handlePhotoUpload}
                />
              )}
            </>
          )}

        </div>

        <Footer />
      </div>
    </div>
  );
};

export default ProfileTab;
